import React, { useEffect } from "react";
import { Button, Chip, Stack, Typography } from "@mui/material";
import { useDevopsChat } from "../hooks/useDevopsAgentChat";
import { useAuth } from "../contexts/AuthContext";
import { useDevopsWorkspace } from "../contexts/DevopsWorkspaceContext";
import { isDevopsOperator, isEnvironmentManager } from "../utils/appRoles";

const QUICK_PROMPTS = [
  "List environments in this tenant and summarize which applications are mapped into each.",
  "What configuration is scoped to the current environment versus specific applications?",
  "Draft a pending release for the selected environment after I confirm the version and changelog.",
];

const DevopsAgentHubPage: React.FC = () => {
  const { sendMessage } = useDevopsChat();
  const { openAgent } = useDevopsWorkspace();
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const envManager = isEnvironmentManager(roles);
  const operator = isDevopsOperator(roles);

  useEffect(() => {
    openAgent();
  }, [openAgent]);

  const ask = (prompt: string) => {
    openAgent();
    sendMessage(prompt);
  };

  return (
    <>
      <Typography variant="h5" fontWeight={600}>
        DevOps agent
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        Query environments, applications, configurations, and releases. The agent opens in a
        drawer from the right. Mutations run only after you confirm. Writes follow the role
        split: environment managers own environments and env-level config; DevOps operators own
        applications, app-level config, and releases.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {envManager && <Chip size="small" color="primary" label="Environment manager" />}
        {operator && <Chip size="small" color="secondary" label="DevOps operator" />}
      </Stack>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Suggested questions
      </Typography>
      <Stack spacing={1} alignItems="flex-start">
        {QUICK_PROMPTS.map((prompt) => (
          <Button
            key={prompt}
            size="small"
            variant="outlined"
            onClick={() => ask(prompt)}
            sx={{ textAlign: "left", justifyContent: "flex-start" }}
          >
            {prompt}
          </Button>
        ))}
      </Stack>
    </>
  );
};

export default DevopsAgentHubPage;
